import React, { useState, useEffect } from 'react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import CalendarView from './CalendarView';

const DatePicker = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onPresetSelect,
  className = "",
  error = null
}) => {
  const [showPresets, setShowPresets] = useState(false);
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);
  const [showCalendarView, setShowCalendarView] = useState(false);

  // Preset date ranges
  const presetRanges = [
    {
      label: 'Today',
      getDates: () => {
        const today = new Date();
        return { start: today, end: today };
      }
    },
    {
      label: 'Yesterday',
      getDates: () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        return { start: yesterday, end: yesterday };
      }
    },
    {
      label: 'Last 7 days',
      getDates: () => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 6);
        return { start, end };
      }
    },
    {
      label: 'Last 30 days',
      getDates: () => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 29);
        return { start, end };
      }
    },
    {
      label: 'This month',
      getDates: () => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return { start, end };
      }
    },
    {
      label: 'Last month',
      getDates: () => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        return { start, end };
      }
    },
    {
      label: 'This year',
      getDates: () => {
        const now = new Date();
        const start = new Date(now.getFullYear(), 0, 1);
        const end = new Date(now.getFullYear(), 11, 31);
        return { start, end };
      }
    }
  ];

  const formatDate = (date) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  const formatDisplayDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handlePresetSelect = (preset) => {
    const { start, end } = preset.getDates();
    onStartDateChange(start);
    onEndDateChange(end);
    setShowPresets(false);
    if (onPresetSelect) {
      onPresetSelect(preset.label);
    }
  };

  const handleDateChange = (date, type) => {
    if (type === 'start') {
      onStartDateChange(date);
    } else {
      onEndDateChange(date);
    }
  };

  const handleCalendarDateSelect = (date, type) => {
    if (type === 'start') {
      onStartDateChange(date);
    } else {
      onEndDateChange(date);
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Preset Range Buttons */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Quick Select
        </label>
        <div className="flex flex-wrap gap-2">
          {presetRanges.map((preset, index) => (
            <button
              key={index}
              onClick={() => handlePresetSelect(preset)}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-[#075e54] transition-colors"
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Custom Date Range */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            Custom Date Range
          </label>
          <button
            onClick={() => setShowCalendarView(true)}
            className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-[#075e54] bg-green-50 border border-green-200 rounded-md hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
          >
            <CalendarIcon className="h-3 w-3" />
            <span>Calendar View</span>
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Start Date */}
          <div className="relative">
            <label htmlFor="startDate" className="block text-xs font-medium text-gray-600 mb-1">
              Start Date
            </label>
            <div className="relative">
              <input
                type="date"
                id="startDate"
                value={formatDate(startDate)}
                onChange={(e) => handleDateChange(e.target.value ? new Date(e.target.value) : null, 'start')}
                className={`w-full px-3 py-2 pl-10 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-[#075e54] ${
                  error ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              <CalendarIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
            {startDate && (
              <p className="text-xs text-gray-500 mt-1">
                {formatDisplayDate(startDate)}
              </p>
            )}
          </div>

          {/* End Date */}
          <div className="relative">
            <label htmlFor="endDate" className="block text-xs font-medium text-gray-600 mb-1">
              End Date
            </label>
            <div className="relative">
              <input
                type="date"
                id="endDate"
                value={formatDate(endDate)}
                onChange={(e) => handleDateChange(e.target.value ? new Date(e.target.value) : null, 'end')}
                className={`w-full px-3 py-2 pl-10 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-[#075e54] ${
                  error ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              <CalendarIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
            {endDate && (
              <p className="text-xs text-gray-500 mt-1">
                {formatDisplayDate(endDate)}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Calendar View Modal */}
      <CalendarView
        selectedStartDate={startDate}
        selectedEndDate={endDate}
        onDateSelect={handleCalendarDateSelect}
        isOpen={showCalendarView}
        onClose={() => setShowCalendarView(false)}
      />
    </div>
  );
};

export default DatePicker;
